export const CAPTACAO_RUN_STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'enqueued', label: 'Enfileirada' },
  { value: 'processing', label: 'Processando' },
  { value: 'processed', label: 'Concluida' },
  { value: 'failed', label: 'Falhou' },
  { value: 'cancelled', label: 'Cancelada' },
];

const RUN_STATUS_LABELS = CAPTACAO_RUN_STATUS_OPTIONS.reduce((acc, option) => {
  if (option.value) acc[option.value] = option.label;
  return acc;
}, {});

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const numberValue = value => Number(value || 0);

const arrayValue = value => (Array.isArray(value) ? value : []);

const objectValue = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

export const runStatusLabel = status =>
  RUN_STATUS_LABELS[status] || status || '-';

export const withCaptacaoRunAuditFields = (run, rawRun = {}) => ({
  ...run,
  sourceUrl: firstPresent(
    rawRun.source_url,
    rawRun.sourceUrl,
    run.sourceUrl,
    ''
  ),
  errorMessage: firstPresent(
    rawRun.error_message,
    rawRun.errorMessage,
    run.errorMessage,
    ''
  ),
  result: objectValue(firstPresent(rawRun.result, run.result, {})),
  checkpoint: objectValue(firstPresent(rawRun.checkpoint, run.checkpoint, {})),
});

export const sourceLabelForRun = run =>
  firstPresent(
    run?.source?.name,
    run?.source?.source_key,
    run?.portal,
    run?.sourceUrl,
    'Origem nao informada'
  );

export const runTimestamp = run =>
  firstPresent(run?.completedAt, run?.updatedAt, run?.createdAt, '');

export const canRetryRun = run => {
  const retry = run?.operationalSummary?.readiness?.retry || {};
  return Boolean(retry.ready) || numberValue(run?.retryableRawRecordsCount) > 0;
};

export const canReconcileRun = run => {
  const readiness = run?.operationalSummary?.readiness || {};
  return Boolean(
    run?.sourceQuarantined || readiness?.quarantine_release?.ready
  );
};

export const buildRunSummary = (runs, meta = {}) => {
  const list = arrayValue(runs);
  return {
    total: numberValue(meta.total_count || list.length),
    active: list.filter(run => run.active).length,
    failed: list.filter(run => run.status === 'failed').length,
    retryable: list.reduce(
      (sum, run) => sum + numberValue(run.retryableRawRecordsCount),
      0
    ),
    quarantined: list.filter(run => run.sourceQuarantined).length,
  };
};

const issueMessage = issue =>
  firstPresent(
    issue?.message,
    issue?.detail,
    issue?.code,
    issue?.reason,
    'Erro de integridade sem mensagem'
  );

const normalizeIssue = (issue, fallback = {}) => ({
  code: firstPresent(
    issue?.code,
    issue?.reason_code,
    fallback.code,
    'integrity_error'
  ),
  message: issueMessage(issue),
  severity: firstPresent(issue?.severity, fallback.severity, 'warning'),
  suggestion: firstPresent(
    issue?.suggestion,
    issue?.action,
    fallback.suggestion,
    ''
  ),
  itemId: firstPresent(
    issue?.item_id,
    issue?.raw_record_id,
    fallback.itemId,
    ''
  ),
});

export const buildRunIntegrity = run => {
  const summary = run?.operationalSummary || {};
  const rawStatusCounts = objectValue(run?.rawStatusCounts);
  const rawErrors = arrayValue(run?.latestRawRecords).flatMap(record => {
    const errors = [
      ...arrayValue(record.errors),
      ...arrayValue(record.fieldErrors),
    ];
    return errors.map(error =>
      normalizeIssue(error, {
        itemId: record.id,
        severity: record.status === 'failed' ? 'critical' : 'warning',
      })
    );
  });
  const reportIssues = [
    ...arrayValue(summary.blockers),
    ...arrayValue(summary.warnings),
  ].map(issue => normalizeIssue(issue));
  const failedCount = numberValue(rawStatusCounts.failed);
  const quarantinedCount = numberValue(rawStatusCounts.quarantined);
  const syntheticIssues = [
    failedCount
      ? {
          code: 'raw_records_failed',
          message: `${failedCount} itens falharam neste run.`,
          severity: 'critical',
        }
      : null,
    quarantinedCount
      ? {
          code: 'raw_records_quarantined',
          message: `${quarantinedCount} itens estao em quarentena.`,
          severity: 'warning',
        }
      : null,
  ].filter(Boolean);
  const errors = [...reportIssues, ...rawErrors, ...syntheticIssues];

  return {
    health: summary?.run_health?.health || (errors.length ? 'attention' : 'ok'),
    status: run?.status || 'unknown',
    errorCount: errors.length,
    retryableCount: numberValue(run?.retryableRawRecordsCount),
    quarantinedCount,
    failedCount,
    errors,
  };
};

const agentLabelForRecord = (record, run) =>
  firstPresent(
    record?.metadata?.writer,
    record?.metadata?.agent,
    record?.metadata?.agent_name,
    run?.result?.agent,
    run?.checkpoint?.agent,
    'Importador de captacao'
  );

const redactedPayloadEntriesForRecord = record => [
  {
    key: 'raw_payload',
    value: firstPresent(record?.redaction?.raw_payload, 'not_returned'),
  },
  {
    key: 'raw_digest',
    value: firstPresent(record?.raw_digest, 'sem digest'),
  },
  {
    key: 'schema_digest',
    value: firstPresent(record?.schema_digest, 'sem schema'),
  },
  {
    key: 'source_record_uid',
    value: firstPresent(record?.source_record_uid, record?.external_id, '-'),
  },
];

export const buildRunProvenanceItems = run =>
  arrayValue(run?.latestRawRecords).map(record => {
    const reconciliation = objectValue(record.reconciliation);
    const imovel = reconciliation.imovel || {};

    return {
      id: record.id,
      source: sourceLabelForRun(run),
      runId: run?.id || record.lais_import_run_id || '-',
      timestamp: firstPresent(
        record.received_at,
        record.observed_at,
        runTimestamp(run)
      ),
      agent: agentLabelForRecord(record, run),
      status: record.status,
      externalId: firstPresent(
        record.external_id,
        record.source_record_uid,
        record.id
      ),
      imovelId: firstPresent(record.imovel_id, imovel.id, ''),
      listingIds: arrayValue(reconciliation.listings).map(
        listing => listing.id
      ),
      payloadEntries: redactedPayloadEntriesForRecord(record),
      redaction: objectValue(record.redaction),
      fieldErrors: arrayValue(record.fieldErrors),
    };
  });
